import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { scrollNavTarget } from '../lib/scrollToSection';

function scrollPageTop() {
  const root = document.documentElement;
  const previousBehavior = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  root.style.scrollBehavior = previousBehavior;
}

/**
 * Owns all route scrolling:
 * - regular page links start at the top;
 * - hash links wait for their destination to mount, then account for the fixed nav.
 */
export default function ScrollToHash() {
  const { pathname, hash, key } = useLocation();
  const previousPathRef = useRef(pathname);

  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useEffect(() => {
    const isSamePage = previousPathRef.current === pathname;
    previousPathRef.current = pathname;

    if (!hash) {
      scrollPageTop();
      const frame = window.requestAnimationFrame(scrollPageTop);
      const timer = window.setTimeout(scrollPageTop, 60);
      return () => {
        window.cancelAnimationFrame(frame);
        window.clearTimeout(timer);
      };
    }

    const id = decodeURIComponent(hash.slice(1));
    if (!id) return undefined;

    let cancelled = false;
    let attempts = 0;
    let retryTimer;

    const tryScroll = () => {
      if (cancelled) return;

      const target = document.getElementById(id);
      if (target) {
        scrollNavTarget(target, isSamePage ? 'smooth' : 'auto');
        return;
      }

      attempts += 1;
      if (attempts < 30) {
        retryTimer = window.setTimeout(tryScroll, 50);
      }
    };

    retryTimer = window.setTimeout(tryScroll, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
    };
  }, [pathname, hash, key]);

  return null;
}
